import React from 'react';
import { IMsalContext, useMsal } from "@azure/msal-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IDataSampleSummaryItem } from "../../../Models/IDataSampleSummaryItem";
import { IUnitConfiguration } from "../../../Models/IUnitConfiguration";
import { ApiService } from "../../../Services/ApiService";
import { getFormattedDate } from "../../../Utils/UnitsHelper";

// Material UI imports
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { createFlexStyles } from '../../../theme/layoutStyles';

/**
 * DataSampleList component - Material UI version
 * Data sample list component for displaying the data samples 
 */
export const DataSampleList: React.FC<{
    dataSamples: IDataSampleSummaryItem[],
    downloadingSample: string,
    downloadDataSampleAsCsv: any,
    unitInputConfig: IUnitConfiguration,
    onDelete?: any
}> = ({
    dataSamples,
    downloadingSample,
    downloadDataSampleAsCsv,
    unitInputConfig,
    onDelete
}) => {
    const theme = useTheme();
    const flexStyles = createFlexStyles(theme);
    const navigate = useNavigate();
    // const params = useParams();
    
    const ctx: IMsalContext = useMsal();
    const apiService = useMemo(() => new ApiService(ctx), [ctx]);

    const [usedFor, setUsedFor] = useState('');
    const [datasetName, setDatasetName] = useState('');
    const [isModalOpen, setIsModalOpen] = useState('');
    const [isLocking, setIsLocking] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState('');

    // Styles
    const styles = {
        // Container styles
        itemContainer: {
            display: 'flex',
            padding: 0,
            marginBottom: theme.spacing(1),
            position: 'relative',
            borderRadius: theme.shape.borderRadius,
            '&:hover': {
                backgroundColor: theme.palette.action.hover
            }
        },
        actionsContainer: {
            ...flexStyles.flexCenter,
            flexDirection: 'row',
            padding: theme.spacing(1),
            minWidth: '120px'
        },
        contentContainer: {
            ...flexStyles.flexCenter,
            flex: 1,
            padding: theme.spacing(1, 2),
            cursor: 'pointer',
            borderRadius: theme.shape.borderRadius,
            '&:hover': {
                backgroundColor: theme.palette.action.hover
            }
        },
        
        // Badge styles
        lockedBadge: {
            position: 'absolute',
            left: '80px',
            top: '5px',
            zIndex: 1
        },
        
        // Content sections
        contentSection: {
            flex: 1,
            minWidth: 0
        },
        statusSection: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: theme.spacing(0.5)
        },
        
        // Modal styles
        modalHeader: {
            ...flexStyles.flexCenterBetween,
            borderBottom: `1px solid ${theme.palette.divider}`
        },
        modalTable: {
            width: '100%',
            marginTop: theme.spacing(2)
        },
        modalTableCell: {
            border: 'none',
            padding: theme.spacing(1, 0)
        },
        modalActions: {
            ...flexStyles.flexCenterGap1,
            marginTop: theme.spacing(2)
        },
        
        // Action button styles
        downloadButton: {
            color: theme.palette.primary.main
        },
        completeButton: {
            color: theme.palette.success.main
        },
        deleteButton: {
            color: theme.palette.error.main
        },
        
        // Text styles
        label: {
            fontWeight: 500,
            fontSize: '0.875rem',
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(0.5)
        },
        value: {
            fontSize: '0.875rem',
            color: theme.palette.text.primary
        },
        clickableValue: {
            fontSize: '0.875rem',
            color: theme.palette.text.primary,
            cursor: 'pointer',
            fontWeight: 500
        }
    };

    // Show modal
    function showModal(item: IDataSampleSummaryItem) {
        setDatasetName(item.datasetName || '');
        setUsedFor(item.usedFor || '');
        setIsModalOpen(item.samplingId);
    }

    // Hide modal
    function hideModal() {
        setIsModalOpen('');
    }

    // Lock record and set dataset name and used for values to the item values 
    async function lockRecord(item: IDataSampleSummaryItem) {
        setIsLocking(true);
        let body: any = {}
        body.id = item.id;
        body.samplingId = item.samplingId;
        body.datasetName = datasetName;

        const response = await apiService.postAsync(body, `modeling/lock`);
        if (!response.ok) {
            setIsLocking(false);
            throw Error(`Could not lock record. ${response.status} ${response.statusText}`);
        }
        var result = await response.json();
        item.id = result.id;
        item.datasetName = result.datasetName;
        item.usedFor = result.usedFor;
        item.lockedStatus = 0; //for now its default need to decide which status we need here
        item.locked = true;
        setIsLocking(false);
        hideModal();
    }

    // Go to design page
    function goToDesign(item: any) {
        unitInputConfig.samplingId = item.samplingId;
        unitInputConfig.prefferedUnit = item.prefferedUnit;
        navigate(`/modeling/${item.samplingId}/datasamples`, { state: { unitInputConfig } });
    }

    // Delete data samples 
    const deleteDataSamples = async (samplingId: string) => {
        setIsDeleting(true);
        const response = await apiService.delete(`modeling/datasamples/${samplingId}`);
        if (!response.ok) {
            setShowDeleteConfirmation('');
            throw Error(`Could not delete record. ${response.status} ${response.statusText}`);
        }
        setIsDeleting(false);
        setShowDeleteConfirmation('');
        onDelete && onDelete();
    }

    return (
        <>
            {dataSamples?.map((item, index) => {
                const currentModalOpen = isModalOpen === item.samplingId;
                
                return (
                    <Paper key={`dataSample-${index}`} sx={styles.itemContainer} elevation={3}>
                        {/* Action buttons */}
                        <Box sx={styles.actionsContainer}>
                            <Tooltip title="Download data sample">
                                <Box>
                                    {downloadingSample === item.samplingId ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <IconButton 
                                            onClick={() => downloadDataSampleAsCsv(item.samplingId)} 
                                            disabled={downloadingSample === item.samplingId}
                                            sx={styles.downloadButton}
                                        >
                                            <DownloadIcon />
                                        </IconButton>
                                    )}
                                </Box>
                            </Tooltip>
                            
                            <Tooltip title={item.todo === 0 ? 'Mark data sample as ready for training' : 'All samples must be completed in order to mark the sample as completed.'}>
                                <Box>
                                    <IconButton 
                                        sx={styles.completeButton}
                                        disabled={downloadingSample === item.samplingId || item.todo > 0}
                                        onClick={() => item.todo === 0 ? showModal(item) : undefined}
                                    >
                                        <CheckCircleIcon />
                                    </IconButton>
                                </Box>
                            </Tooltip>

                            <IconButton 
                                onClick={() => setShowDeleteConfirmation(item.samplingId)} 
                                sx={styles.deleteButton}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>

                        {/* Locked badge */}
                        {item.locked && (
                            <Chip
                                icon={<LockIcon />}
                                label={`Locked - ${item.usedFor} ${item.usedFor ? '🚀' : '🧪'}`}
                                variant="outlined"
                                size="small"
                                sx={styles.lockedBadge}
                            />
                        )}

                        {/* Main content */}
                        <Box sx={styles.contentContainer} onClick={() => goToDesign(item)}>
                            <Box sx={styles.contentSection}>
                                <Typography sx={styles.label}>SamplingId</Typography>
                                <Typography sx={styles.clickableValue}>{item.samplingId}</Typography>
                            </Box>
                            
                            <Box sx={styles.contentSection}>
                                <Typography sx={styles.label}>Uploaded by</Typography>
                                <Typography sx={styles.value}>{item.user}</Typography>
                            </Box>
                            
                            <Box sx={styles.contentSection}>
                                <Typography sx={styles.label}>Created</Typography>
                                <Typography sx={styles.value}>{getFormattedDate(item.created.toString())}</Typography>
                            </Box>
                            
                            <Box sx={styles.contentSection}>
                                <Typography sx={styles.label}>Modified</Typography>
                                <Typography sx={styles.value}>
                                    {item.lastUpdate ? getFormattedDate(item.lastUpdate.toString()) : 'Never'}
                                </Typography>
                            </Box>
                            
                            <Box sx={styles.statusSection}>
                                <Typography sx={styles.label}>Status</Typography>
                                <Box sx={flexStyles.flexCenterGap1}>
                                    <Chip label={`${item.todo} To do`} size="small" />
                                    <Chip label={`${item.done} Done`} size="small" color="success" />
                                </Box>
                            </Box>
                        </Box>

                        {/* Completion Modal */}
                        <Dialog 
                            open={currentModalOpen} 
                            onClose={hideModal}
                            maxWidth="sm"
                            fullWidth
                        >
                            <DialogTitle sx={styles.modalHeader}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Completing {item.samplingId}</Typography>
                                <IconButton onClick={hideModal} size="small">
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>
                            
                            <DialogContent>
                                <Box 
                                    component="form" 
                                    onSubmit={(e: React.FormEvent) => { e.preventDefault(); return lockRecord(item) }}
                                >
                                    <Table sx={styles.modalTable}>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={styles.modalTableCell}>
                                                    <Typography sx={styles.label}>Uploaded by</Typography>
                                                </TableCell>
                                                <TableCell sx={styles.modalTableCell}>
                                                    <Typography sx={styles.value}>{item.user}</Typography>
                                                </TableCell>
                                            </TableRow>
                                            
                                            <TableRow>
                                                <TableCell sx={styles.modalTableCell}>
                                                    <Typography sx={styles.label}>Dataset</Typography>
                                                </TableCell>
                                                <TableCell sx={styles.modalTableCell}>
                                                    <TextField
                                                        required
                                                        size="small"
                                                        fullWidth
                                                        value={datasetName}
                                                        onChange={(e) => setDatasetName(e.target.value)}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            
                                            <TableRow>
                                                <TableCell sx={styles.modalTableCell}>
                                                    <Typography sx={styles.label}>Used for</Typography>
                                                </TableCell>
                                                <TableCell sx={styles.modalTableCell}>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        value={usedFor}
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            
                                            <TableRow>
                                                <TableCell sx={styles.modalTableCell}></TableCell>
                                                <TableCell sx={styles.modalTableCell}>
                                                    <Box sx={styles.modalActions}>
                                                        <Tooltip title={
                                                            <Box component="span">
                                                                Marks the data set as <strong>complete</strong>, meaning the data set should be picked up by the Machine Learning pipelines.
                                                            </Box>
                                                        }>
                                                            <Box>
                                                                <Button
                                                                    type="submit"
                                                                    variant="contained"
                                                                    disabled={isLocking}
                                                                    startIcon={<CheckCircleIcon />}
                                                                >
                                                                    Mark as completed
                                                                </Button>
                                                            </Box>
                                                        </Tooltip>
                                                        <InfoIcon color="primary" />
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Box>
                            </DialogContent>
                        </Dialog>
                    </Paper>
                );
            })}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={!!showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation('')}
                maxWidth="sm"
            >
                <DialogTitle>Delete data sample set</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete data sample <code>{showDeleteConfirmation}</code>?
                    </DialogContentText>
                    <DialogContentText>
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    {isDeleting ? (
                        <Box sx={{ ...flexStyles.flexCenterGap2, p: 1 }}>
                            <CircularProgress size={20} />
                            <Typography>Deleting data sample set...</Typography>
                        </Box>
                    ) : (
                        <>
                            <Button onClick={() => setShowDeleteConfirmation('')}>
                                Cancel
                            </Button>
                            <Button 
                                onClick={() => deleteDataSamples(showDeleteConfirmation)}
                                color="error"
                                disabled={isDeleting}
                            >
                                Delete
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}; 